import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose, IoAlertCircleOutline, IoCheckmarkCircleOutline, IoCloudUploadOutline, IoTrashOutline, IoImageOutline, IoVideocamOutline } from "react-icons/io5";
import { useReopenOcurrence, useUpdateOccurrenceMedia } from "@/hooks/useOcurrences";
import { useGeneratePresignedUrl } from "@/hooks/useMedia";
import type { ReopenReason } from "@/dtos/ReopenOcurrenceDto";
import type { MediaDto } from "@/dtos/MediaDto";
import { Spinner } from "@/components/Spinner";
import toast from "react-hot-toast";
import axios from "axios";

type ReopenModalProps = {
  occurrenceId: string;
  occurrenceProtocol: string;
  isOpen: boolean;
  onClose: () => void;
  maxReopens: number;
  currentReopens: number;
};

const REOPEN_REASONS: { value: ReopenReason; label: string }[] = [
  { value: "NOT_SOLVED", label: "Problema não foi resolvido" },
  { value: "PARTIALLY_SOLVED", label: "Problema resolvido parcialmente" },
  { value: "PROBLEM_RECURRED", label: "Problema voltou a ocorrer" },
  { value: "POOR_QUALITY_SERVICE", label: "Serviço executado com má qualidade" },
  { value: "OTHER", label: "Outro" },
];

export const ReopenModal = ({
  occurrenceId,
  occurrenceProtocol,
  isOpen,
  onClose,
  maxReopens,
  currentReopens,
}: ReopenModalProps) => {
  const [reason, setReason] = useState<ReopenReason | "">("");
  const [description, setDescription] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedMedia, setUploadedMedia] = useState<MediaDto[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const reopenOcurrence = useReopenOcurrence();
  const updateOccurrenceMedia = useUpdateOccurrenceMedia();
  const generatePresignedUrl = useGeneratePresignedUrl();

  const handleClose = () => {
    if (!reopenOcurrence.isPending && !isUploading) {
      setReason("");
      setDescription("");
      setSelectedFiles([]);
      setUploadedMedia([]);
      setShowSuccess(false);
      onClose();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith("image/") || file.type.startsWith("video/");
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      
      if (!isValidType) {
        toast.error(`${file.name}: Apenas imagens e vídeos são permitidos`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name}: Arquivo deve ter no máximo 50MB`);
        return false;
      }
      return true;
    });

    const totalFiles = selectedFiles.length + validFiles.length;
    if (totalFiles > 5) {
      toast.error("Máximo de 5 arquivos permitidos");
      return;
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (): Promise<MediaDto[]> => {
    if (selectedFiles.length === 0) return [];

    setIsUploading(true);
    const mediaObjects: MediaDto[] = [];

    try {
      for (const file of selectedFiles) {
        const mediaType = file.type.startsWith("image/") ? "IMAGE" : "VIDEO";
        
        // Generate presigned URL
        const presignedData = await generatePresignedUrl.mutateAsync({
          fileName: file.name,
          type: mediaType,
          mimeType: file.type,
          size: file.size,
        });

        // Upload to S3
        await axios.put(presignedData.uploadUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        // Store complete media object
        mediaObjects.push({
          key: presignedData.key,
          type: mediaType as "IMAGE" | "VIDEO",
          mimeType: file.type,
          size: file.size,
        });
      }

      setUploadedMedia(mediaObjects);
      return mediaObjects;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erro ao fazer upload dos arquivos");
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!reason) {
      toast.error("Selecione o motivo da reabertura");
      return;
    }

    if (description.trim().length < 20) {
      toast.error("A descrição deve ter no mínimo 20 caracteres");
      return;
    }

    if (description.trim().length > 500) {
      toast.error("A descrição deve ter no máximo 500 caracteres");
      return;
    }

    try {
      // 1. Reopen occurrence first
      await reopenOcurrence.mutateAsync({
        occurrenceId,
        reason,
        description: description.trim(),
      });

      // 2. Upload files and update media if any
      if (selectedFiles.length > 0) {
        const mediaObjects = await uploadFiles();
        await updateOccurrenceMedia.mutateAsync({
          occurrenceId,
          media: mediaObjects,
        });
      }

      setShowSuccess(true);
      
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erro ao reabrir ocorrência");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
              >
                <IoCheckmarkCircleOutline className="text-6xl text-green-500 mx-auto mb-4" />
              </motion.div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Ocorrência Reaberta!
              </h3>
              <p className="text-gray-600">
                Sua solicitação foi enviada e será reanalisada pelo setor responsável.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Reabrir Ocorrência
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Protocolo: {occurrenceProtocol}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  disabled={reopenOcurrence.isPending}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                >
                  <IoClose className="text-2xl text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* Warning Alert */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex gap-3">
                  <IoAlertCircleOutline className="text-xl text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-orange-900 mb-1">
                      Atenção
                    </p>
                    <p className="text-orange-800">
                      Você já reabriu esta ocorrência {currentReopens} {currentReopens === 1 ? "vez" : "vezes"}.
                      O limite máximo é de {maxReopens} reaberturas.
                    </p>
                  </div>
                </div>

                {/* Reason Select */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Motivo da Reabertura *
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value as ReopenReason)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={reopenOcurrence.isPending}
                  >
                    <option value="">Selecione um motivo</option>
                    {REOPEN_REASONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição Detalhada *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explique detalhadamente o que aconteceu..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows={6}
                    maxLength={500}
                    disabled={reopenOcurrence.isPending}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className={`text-xs ${
                      description.length < 20 
                        ? "text-red-600" 
                        : description.length >= 500 
                        ? "text-orange-600" 
                        : "text-gray-500"
                    }`}>
                      {description.length < 20 
                        ? `Mínimo 20 caracteres (faltam ${20 - description.length})` 
                        : "Mínimo atingido"}
                    </span>
                    <span className={`text-xs ${
                      description.length > 500 ? "text-red-600" : "text-gray-500"
                    }`}>
                      {description.length}/500
                    </span>
                  </div>
                </div>

                {/* Media Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Anexar Fotos/Vídeos (Opcional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Máximo 5 arquivos • Imagens ou vídeos • Até 50MB cada
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isUploading || reopenOcurrence.isPending}
                  />

                  {/* Upload Button */}
                  {selectedFiles.length < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading || reopenOcurrence.isPending}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-gray-600"
                    >
                      <IoCloudUploadOutline className="text-xl" />
                      <span className="text-sm font-medium">
                        Clique para adicionar arquivos
                      </span>
                    </button>
                  )}

                  {/* File Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex-shrink-0">
                            {file.type.startsWith("image/") ? (
                              <IoImageOutline className="text-2xl text-blue-500" />
                            ) : (
                              <IoVideocamOutline className="text-2xl text-purple-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            disabled={isUploading || reopenOcurrence.isPending}
                            className="flex-shrink-0 p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <IoTrashOutline className="text-lg text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>O que acontece após a reabertura?</strong>
                  </p>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                    <li>A ocorrência voltará para o setor responsável</li>
                    <li>O prazo de atendimento (SLA) será reiniciado</li>
                    <li>Você poderá acompanhar o novo andamento</li>
                    <li>Uma nova avaliação poderá ser feita após conclusão</li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={handleClose}
                  disabled={reopenOcurrence.isPending || isUploading}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={
                    reopenOcurrence.isPending ||
                    isUploading ||
                    !reason ||
                    description.trim().length < 20 ||
                    description.trim().length > 500
                  }
                  className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Spinner />
                      <span>Enviando arquivos...</span>
                    </>
                  ) : reopenOcurrence.isPending ? (
                    <>
                      <Spinner />
                      <span>Reabrindo...</span>
                    </>
                  ) : (
                    "Confirmar Reabertura"
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
